import { parseFetchError, parseGQLErrors, type GQLErrorResponse, type GQLResult } from '#shared/utils/gql/GQLResult'
import type { TypedDocumentString } from '@@fire-b2b/graphql/codegen/graphql'
import type { ExecutionResult } from 'graphql'
import type { FetchResponse } from 'ofetch'

export async function gqlExecute<TData, TVariables>({
  query,
  variables,
  abortSignal,
}: {
  query: TypedDocumentString<TData, TVariables>
  variables: TVariables extends Record<string, never> ? undefined : TVariables
  abortSignal?: AbortSignal
}): Promise<GQLResult<TData>> {
  const auth = useAuth()
  const { $i18n, $csrfFetch } = useNuxtApp()

  let response: FetchResponse<ExecutionResult<TData>>
  try {
    response = await $csrfFetch.raw('/api/gql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/graphql-response+json',
      },
      body: JSON.stringify({
        query,
        variables,
        locale: useShopConfigStore().getGqlLocale($i18n.locale.value),
      }),
      signal: abortSignal,
    })
  } catch (error) {
    if (abortSignal?.aborted) {
      return err({
        type: 'aborted',
        statusCode: 0,
        message: 'The request was aborted.',
        wasAborted: true,
      })
    }

    return err(parseFetchError(error))
  }

  if (!response.ok) {
    if (response.status === 401) {
      await auth.logout()
    }

    return err({
      statusCode: response.status,
      message: `Network error: ${response.status} ${response.statusText}`,
    })
  }

  if (!response._data) {
    return err({
      statusCode: 0,
      message: 'The server did not return a valid GraphQL response.',
    })
  }

  const executionResult: ExecutionResult<TData> = response._data

  if ('errors' in executionResult) {
    return err(parseGQLErrors(executionResult as unknown as GQLErrorResponse))
  }

  return ok(executionResult.data!)
}
